<template>
  <div>
    <div class="space-y-6 py-4 border-b" style="border-color: #0000001f">
      <div
        class="flex items-start p-3 w-full mt-6 rounded-lg border"
        style="background-color: #e993231f; border-color: #e9932329"
      >
        <Icon icon-name="icon-info" size="xl" class="text-carrotOrange mr-1" />
        <span class="text-xs">
          Set up and manage all public, state, and company-declared holidays
          observed within your organization. These holidays are automatically
          applied during leave calculations to ensure employees are not deducted
          for days that fall on official non-working days. Maintain an accurate
          holiday calendar to support seamless scheduling, correct entitlement
          tracking, and efficient workforce planning across all locations.
        </span>
      </div>
      <div class="flex justify-end">
        <button
          class="bg-dynamicBackBtn rounded-lg px-4 py-3 text-white font-semibold text-sm"
          @click="handleCreateNew"
        >
          Create New
        </button>
      </div>
    </div>

    <div class="mt-6">
      <div v-if="!loading && activeTableItems.length === 0" style="height: 60vh" class="flex items-center justify-center">
        <NoLeave
          title="No Public Holiday Added Yet"
          description="Create your holiday list to ensure accurate leave calculations."
        />
      </div>

      <div v-if="activeTableItems.length > 0">
        <div class="mx-3 -mt-1">
          <c-card>
            <card-footer
              reloadcard
              class="px-6 pb-6"
              @reload="queryUpdate()"
              @sortType="$queryBuilder({ sort: $event })"
              @searchResult="handleSearch($event)"
            />
          </c-card>

          <sTable
            :headers="tableHeader"
            :items="activeTableItems"
            style="width: 100%"
            :loading="loading"
            :has-number="false"
            :pagination-list="metaData"
            @page="handlePage($event)"
            @itemsPerPage="handleItemsPerPage($event)"
            page-sync
          >
            <template v-slot:item="{ item }">
              <div v-if="item.holidayName">
                {{ item.data.holidayName }}
              </div>
              <div v-if="item.description">
                {{ item.data.description || '-' }}
              </div>
              <div v-if="item.holidayCategory">
                {{ item.data.holidayCategory || item.data.category || '-' }}
              </div>
              <div v-if="item.date">
                {{ formatDate(item.data.date ) }}
              </div>
              <div v-if="item.locations">
                {{ formatLocations(item.data.locations) }}
              </div>
              <div v-if="item.recurring">
                {{ formatRecurring(item.data.recurring) }}
              </div>
              <div v-else-if="item.id">
                <Menu left top="-150" margin="24" class="my-2 p-0">
                  <template v-slot:title>
                    <icon icon-name="more_icon" size="xs" />
                  </template>
                  <div class="w-48 flex flex-col justify-start items-center gap-3 px-3 py-2">
                    <div class="w-full" @click="handleOptionClick('edit', item.data)">
                      <div class="w-full flex justify-start items-center gap-2 cursor-pointer">
                        <Icon icon-name="edit" class-name="text-blueCrayola" size="xs" />
                        <p class="font-normal text-sm text-darkPurple">
                          Edit
                        </p>
                      </div>
                    </div>
                    <div class="w-full" @click="handleOptionClick('view', item.data)">
                      <div class="w-full flex justify-start items-center gap-2 cursor-pointer">
                        <Icon icon-name="icon-eye" style="color: #13B56A;" size="xs" />
                        <p class="font-normal text-sm text-darkPurple">
                          View
                        </p>
                      </div>
                    </div>
                    <div class="w-full" @click="handleOptionClick('delete', item.data)">
                      <div class="w-full flex justify-start items-center gap-2 cursor-pointer">
                        <Icon icon-name="delete" class-name="text-flame" size="xs" />
                        <p class="font-normal text-sm text-darkPurple">
                          Delete
                        </p>
                      </div>
                    </div>
                  </div>
                </Menu>
              </div>
            </template>
          </sTable>
        </div>
      </div>
    </div>
    <PublicHolidayDrawer
      ref="publicHolidayDrawerRef"
      @close="queryUpdate"
      @success="queryUpdate"
    />
  </div>
</template>

<script>
import Button from "@/components/Button";
import NoLeave from "./NoLeave";
import CCard from "@scelloo/cloudenly-ui/src/components/card";
import sTable from "@scelloo/cloudenly-ui/src/components/table";
import CardFooter from "@/components/CardFooter";
import Menu from "@/components/Menu";
import Icon from "@/components/Icon";
import PublicHolidayDrawer from "./PublicHolidayDrawer";

export default {
  components: {
    Button,
    NoLeave,
    CCard,
    sTable,
    CardFooter,
    Menu,
    Icon,
    PublicHolidayDrawer,
  },
  data() {
    return {
      tableHeader: [
        { title: "Holiday Name", value: "holidayName" },
        { title: "Description", value: "description" },
        { title: "Holiday Category", value: "holidayCategory" },
        { title: "Date", value: "date" },
        { title: "Location", value: "locations" },
        { title: "Recurring", value: "recurring" },
        { title: "", value: "id", image: true },
      ],
      activeTableItems: [],
      loading: false,
      queryParams: {},
      metaData: {},
      pageNumber: null,
      itemsPerPage: null,
      searchWord: null,
    };
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      try {
        const d = new Date(date);
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return d.toLocaleDateString('en-US', options);
      } catch (error) {
        return date;
      }
    },
    formatLocations(locations) {
      if (!locations || !Array.isArray(locations)) {
        return '';
      }
      
      const locationNames = locations
        .map(loc => loc?.name)
        .filter(Boolean);

      return locationNames.join(', ');
    },
    formatRecurring(recurring) {
      if (recurring === true || recurring === 'yes' || recurring === 'Yes') {
        return 'Yes';
      }
      if (recurring === false || recurring === 'no' || recurring === 'No') {
        return 'No';
      }
      return recurring || '-';
    },
    formatSkipWeekend(skipWeekend) {
      if (skipWeekend === true || skipWeekend === 'yes' || skipWeekend === 'Yes') {
        return 'Yes';
      }
      if (skipWeekend === false || skipWeekend === 'no' || skipWeekend === 'No') {
        return 'No';
      }
      return skipWeekend || '-';
    },
    queryUpdate() {
      const search = this.searchWord ? `&search=${encodeURIComponent(this.searchWord)}` : "";
      const pageNumber = this.pageNumber
        ? `?page=${this.pageNumber}`
        : this.metaData.page
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      const queryString = `${pageNumber}${itemPage}${search}`;
      this.getHolidayLeave(queryString);
    },
    async getHolidayLeave(params = "") {
      this.loading = true;
      try {
        const { data } = await this.$_getPublicHolidays(params);
        const holidays = data?.data || [];

        this.activeTableItems = holidays.map((holiday) => ({
          holidayName: holiday.holidayName,
          description: holiday.description,
          holidayCategory: holiday.holidayCategory || holiday.category,
          date: holiday.fromDate,
          locationIds: holiday.locationIds,
          locations: holiday.locations,
          recurring: holiday.recurring,
          id: holiday.id,
          data: holiday,
          skipWeekend: holiday.skipWeekend,
        }));

        if (data?.meta) {
          this.metaData = data.meta;
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to fetch public holidays. Please try again.";
        this.$toasted.error(errorMessage, { duration: 3000 });
        this.activeTableItems = [];
        this.metaData = {};
      } finally {
        this.loading = false;
      }
    },
    handlePage(pageNumber) {
      this.pageNumber = pageNumber;
      this.queryUpdate();
    },
    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.pageNumber = 1;
      this.queryUpdate();
    },
    handleSearch(searchWord) {
      this.searchWord = searchWord;
      this.pageNumber = 1;
      this.queryUpdate();
    },
    queryBuilder(params, callback) {
      if (params.search !== undefined) {
        this.handleSearch(params.search);
      } else if (params.sort) {
        this.queryParams = { ...this.queryParams, ...params };
        this.queryUpdate();
      } else {
        this.queryParams = { ...this.queryParams, ...params };
        if (callback) {
          callback();
        } else {
          this.queryUpdate();
        }
      }
    },
    handleOptionClick(action, item) {
      if (action === "edit") {
        this.handleEditHoliday(item);
      } else if (action === "view") {
        this.handleViewHoliday(item);
      } else if (action === "delete") {
        this.handleDeleteHoliday(item);
      }
    },
    handleCreateNew() {
      this.$refs.publicHolidayDrawerRef.onOpen(null, "create");
    },
    handleEditHoliday(holiday) {
      this.$refs.publicHolidayDrawerRef.onOpen(holiday, "edit");
    },
    handleViewHoliday(holiday) {
      this.$refs.publicHolidayDrawerRef.onOpen(holiday, "view");
    },
    async handleDeleteHoliday(holiday) {
      if (!holiday || !holiday.id) {
        this.$toasted.error("Invalid holiday data", { duration: 3000 });
        return;
      }

      const confirmed = confirm(`Are you sure you want to delete "${holiday.holidayName || 'this holiday'}"?`);
      if (!confirmed) {
        return;
      }

      try {
        const payload = {
          deletedBy: this.$AuthUser?.id,
        };

        await this.$_deletePublicHoliday({
          requestId: holiday.id,
          payload,
        });

        this.$toasted.success("Holiday deleted successfully", { duration: 3000 });
        await this.queryUpdate();
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete holiday. Please try again.";
        this.$toasted.error(errorMessage, { duration: 3000 });
      }
    },
  },
  async mounted() {
    await this.getHolidayLeave("?page=1");
  },
};
</script>

<style lang="scss" scoped></style>
